/**
 * Default Calendar Configuration
 * Provides default values for YearlyCalendar component
 */
import { CalendarConfig } from '../../app/contracts/YearlyCalendarContracts';

/**
 * Default calendar configuration
 */
export const DEFAULT_CALENDAR_CONFIG: CalendarConfig = {
  // View mode
  datepickerMode: 'richcc-year',
  
  // Date boundaries
  minDate: undefined,
  maxDate: undefined,
  
  // Week settings
  startingDay: 0, // Sunday
  showWeeks: false,
  nonWorkDays: [], // Populated dynamically from user's work preferences
  
  // Display settings
  navigation: true,
  headerLogo: undefined,
  headerText: 'Calendar',
  footerText: undefined,
  
  // Date format patterns
  formatDay: 'd',
  formatDayHeader: 'EEE',
  formatDayTitle: 'MMMM yyyy',
  formatMonth: 'MMMM',
  formatMonthTitle: 'yyyy',
  formatYear: 'yyyy',
  formatDayHeaderFull: 'EEEE',
  
  // Event settings
  maxVisibleEvents: 2,
  showAddEventOnHover: true,
  allowEventClick: true,
  
  // Callback placeholders (set via props)
  customClass: undefined,
  customAriaLabel: undefined,
  customIconClass: undefined,
};

/**
 * Create a merged configuration with defaults
 */
export const createCalendarConfig = (
  partialConfig?: Partial<CalendarConfig>
): CalendarConfig => {
  if (!partialConfig) {
    return { ...DEFAULT_CALENDAR_CONFIG };
  }
  
  return {
    ...DEFAULT_CALENDAR_CONFIG,
    ...partialConfig,
  };
};

/**
 * US locale configuration
 */
export const US_CALENDAR_CONFIG: Partial<CalendarConfig> = {
  startingDay: 0, // Sunday
  nonWorkDays: ['Saturday', 'Sunday'],
  formatDayTitle: 'MMMM yyyy',
  formatMonthTitle: 'yyyy',
};

/**
 * EU locale configuration (week starts Monday)
 */
export const EU_CALENDAR_CONFIG: Partial<CalendarConfig> = {
  startingDay: 1, // Monday
  nonWorkDays: ['Saturday', 'Sunday'],
  formatDayTitle: 'MMMM yyyy',
  formatMonthTitle: 'yyyy',
};

/**
 * Compact display configuration
 */
export const COMPACT_CALENDAR_CONFIG: Partial<CalendarConfig> = {
  showWeeks: false,
  maxVisibleEvents: 1,
  showAddEventOnHover: false,
};

/**
 * Full display configuration (for yearly views)
 */
export const FULL_YEARLY_CONFIG: Partial<CalendarConfig> = {
  showWeeks: true,
  maxVisibleEvents: 2,
  showAddEventOnHover: true,
};

export default DEFAULT_CALENDAR_CONFIG;
